/* Copyright (c) 2024-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Observable } from 'rxjs';
import { ScreenSizes } from './resize-observer.types';

export class ResizeObserverUtils {
    /**
     * Observe the changes to the dimensions of an Element
     *
     * @param element the element on which the Resize Observer will observe
     * @param screenSizes the maximum & minimum values of the height & width
     * @returns Whether the target element has been changed to match the provided screenSizes.
     */
    public static resizeObserver(element: Element, screenSizes: ScreenSizes): Observable<boolean> {
        let resizeObserver: ResizeObserver = null;
        const isSmallScreen$: Observable<boolean> = new Observable<boolean>(subscriber => {
            // Prevent emitting value when when callback is received after subscriber unsubscribes.
            let subscribe = true;
            resizeObserver = new ResizeObserver(entries => {
                const width = entries[0].contentRect.width;
                const height = entries[0].contentRect.height;
                const isSmallScreen =
                    height <= screenSizes.maxHeight && width <= screenSizes.maxWidth && width > screenSizes.minWidth;

                if (subscribe) {
                    subscriber.next(isSmallScreen);
                }
            });

            resizeObserver.observe(element);

            // unsubscribe by subscribers
            return () => {
                subscribe = false;
                resizeObserver?.unobserve(element);
            };
        });
        return isSmallScreen$;
    }

    public static onResize(element: Element, options?: ResizeObserverOptions): Observable<ResizeObserverEntry> {
        return new Observable(observer => {
            let subscribe = true;
            const resizeObserver = new ResizeObserver(entries => {
                if (subscribe) {
                    observer.next(entries[0]);
                }
            });
            resizeObserver.observe(element, options);

            return () => {
                subscribe = false;
                resizeObserver?.unobserve(element);
            };
        });
    }
}
